import { canRemoveMedia, quarantineNotice, type BoardItem, type BoardShot, type Viewer } from "@boem/core/shared";
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { ApiError } from "../api.ts";
import "../media.css";
import { mediaConfig, uploadFile, type MediaConfig, type UploadResult, type UploadTarget } from "../mediaApi.ts";
import { Modal } from "./Modal.tsx";

// Screenshots and screen recordings: the strip on each card, the drop zone,
// and the upload queue used by the "Add screenshot" and "New item" dialogs.

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.mov";
const IMAGE_EXT = /\.(png|jpe?g|webp|gif)$/i;
const VIDEO_EXT = /\.(mp4|webm|mov)$/i;

export function fmtDuration(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return "";
  const t = Math.round(sec);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

const what = (s: Pick<BoardShot, "kind">) => (s.kind === "video" ? "screen recording" : "screenshot");

/** What kind of file the browser thinks this is (advisory - the server checks the bytes). */
function kindOf(f: File): "image" | "video" | null {
  if (/^image\/(png|jpeg|webp|gif)$/.test(f.type) || (!f.type && IMAGE_EXT.test(f.name))) return "image";
  if (/^video\/(mp4|webm|quicktime)$/.test(f.type) || (!f.type && VIDEO_EXT.test(f.name))) return "video";
  if (IMAGE_EXT.test(f.name)) return "image";
  if (VIDEO_EXT.test(f.name)) return "video";
  return null;
}

// -- The strip on a card ----------------------------------------------------------

/** The files on the item itself, or the ones added with one note, each with its place in item.shots (the lightbox steps through them all). */
export const shotsOf = (item: Pick<BoardItem, "shots">, noteId: string | null = null) =>
  item.shots.map((s, i) => ({ s, i })).filter(({ s }) => (s.noteId ?? null) === noteId);

/**
 * A card's screenshots and recordings, with the notices for ones being checked or hidden. With
 * `noteId`: the files added with that note, shown under it (smaller, `note`).
 */
export function ShotStrip({
  item,
  viewer,
  onOpen,
  onRemove,
  onRelease,
  noteId = null,
}: {
  item: BoardItem;
  viewer: Viewer;
  onOpen: (index: number) => void;
  onRemove: (id: string) => void;
  onRelease: (id: string) => void;
  noteId?: string | null;
}) {
  // thumbnails that failed to load (by URL, so a new thumbnail tries again): a tile instead of a 1px sliver
  const [missing, setMissing] = useState<ReadonlySet<string>>(() => new Set());
  const entries = shotsOf(item, noteId);
  if (!entries.length) return null;
  const shots = entries.map((e) => e.s);
  const hidden = shots.filter((s) => s.scan === "quarantined");
  // a direct upload that couldn't be used is its own notice, not "being checked"
  const checking = shots.filter((s) => s.scan === "pending" && !s.scanFailed && !s.failed);
  const failed = shots.filter((s) => s.scan === "pending" && s.scanFailed && !s.failed);
  const unusable = shots.filter((s) => s.failed);
  const may = (s: BoardShot) => canRemoveMedia(viewer, { uploadedById: s.mine ? viewer.id : null });
  const where = noteId ? `a note on item ${item.num}` : `item ${item.num}`;
  return (
    <>
      <div className={`shots${noteId ? " note-shots" : ""}`}>
        {entries.map(({ s, i }, n) => {
          const label = `${s.kind === "video" ? "Screen recording" : "Screenshot"} ${n + 1} for ${where}${s.name ? ` (${s.name})` : ""}`;
          // no thumbnail yet (a direct upload being made ready), one that didn't load, or one that couldn't be used
          const tile = !s.thumb || missing.has(s.thumb);
          return (
            <button
              key={s.id}
              type="button"
              className={`shot shot-${s.scan}${s.kind === "video" ? " shot-video" : ""}${tile ? " shot-missing" : ""}`}
              title={s.failed ? `Couldn't be used: ${s.failed}` : s.scan === "quarantined" ? "Hidden: looks like it contains a credential" : s.scan === "pending" ? "Being checked for credentials" : label}
              aria-label={label}
              disabled={!s.thumb}
              onClick={() => onOpen(i)}
            >
              {tile ? (
                <span className="shot-missing-lbl">
                  {s.processing ? "Getting it ready" : `${s.kind === "video" ? "Recording" : "Screenshot"} ${n + 1}`}
                </span>
              ) : (
                <img src={s.thumb} loading="lazy" decoding="async" alt="" onError={() => setMissing((m) => new Set(m).add(s.thumb))} />
              )}
              {s.kind === "video" && !tile && <span className="shot-play" aria-hidden="true" />}
              {s.kind === "video" && s.duration != null && <span className="shot-dur">{fmtDuration(s.duration)}</span>}
              {s.scan === "pending" && <span className={`shot-badge${s.failed ? " shot-badge-hidden" : ""}`}>{s.failed ? "Not used" : s.scanFailed ? "Not checked" : "Checking…"}</span>}
              {s.scan === "quarantined" && <span className="shot-badge shot-badge-hidden">Hidden</span>}
            </button>
          );
        })}
      </div>
      {hidden.map((s) => (
        <div className="shot-notice hidden-notice" role="status" key={s.id}>
          <span>
            {s.mine
              ? quarantineNotice(s.findings)
              : `Hidden from the team: this ${what(s)} looks like it contains a credential${s.findings.length ? ` (${s.findings.join(", ")})` : ""}. ${
                  s.uploadedBy ? `Added by ${s.uploadedBy}.` : "Imported from the original board."
                }`}
          </span>
          {may(s) && (
            <button type="button" className="xref" onClick={() => onRemove(s.id)}>
              Remove
            </button>
          )}
          {viewer.isAdmin && (
            <button type="button" className="xref" onClick={() => onRelease(s.id)} title="You checked it and it holds no credential">
              Show to everyone
            </button>
          )}
        </div>
      ))}
      {checking.length > 0 && (
        <div className="shot-notice" role="status">
          Checking&hellip; {checking.length === 1 ? `This ${what(checking[0]!)} is` : `${checking.length} files are`} being checked for
          credentials. {checking.some((s) => s.mine) ? "Only you" : "Only the uploader"} and admins can see {checking.length === 1 ? "it" : "them"} until then.
        </div>
      )}
      {failed.map((s) => (
        <div className="shot-notice hidden-notice" role="status" key={s.id}>
          <span>This {what(s)} couldn&rsquo;t be checked for credentials, so only {s.mine ? "you" : "its uploader"} and admins can see it. Remove it and upload it again.</span>
          {may(s) && (
            <button type="button" className="xref" onClick={() => onRemove(s.id)}>
              Remove
            </button>
          )}
        </div>
      ))}
      {unusable.map((s) => (
        <div className="shot-notice hidden-notice" role="status" key={s.id}>
          <span>
            {s.name ? `${s.name}: ` : ""}this {what(s)} arrived but couldn&rsquo;t be used ({s.failed}). Only {s.mine ? "you" : "its uploader"} and admins see this. Remove it, and upload it
            again if it helps.
          </span>
          {may(s) && (
            <button type="button" className="xref" onClick={() => onRemove(s.id)}>
              Remove
            </button>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * A note's screenshots as small thumbnails that open the file in a new tab: for the History, where
 * a note's line shows what was added with it. Only the files this viewer may see (the board payload's).
 */
export function NoteThumbs({ shots, noteId }: { shots: BoardShot[]; noteId: string | undefined }) {
  // a direct upload still being made ready (no thumbnail yet) shows once it's ready
  const mine = noteId ? shots.filter((s) => s.noteId === noteId && s.thumb) : [];
  if (!mine.length) return null;
  return (
    <span className="shots note-shots hist-shots">
      {mine.map((s, n) => (
        <a key={s.id} className={`shot shot-${s.scan}${s.kind === "video" ? " shot-video" : ""}`} href={s.full ?? s.thumb} target="_blank" rel="noopener noreferrer" aria-label={`${s.kind === "video" ? "Screen recording" : "Screenshot"} ${n + 1} of this note${s.name ? ` (${s.name})` : ""}`}>
          <img src={s.thumb} loading="lazy" decoding="async" alt="" />
          {s.kind === "video" && <span className="shot-play" aria-hidden="true" />}
          {s.scan === "pending" && <span className="shot-badge">{s.scanFailed ? "Not checked" : "Checking…"}</span>}
          {s.scan === "quarantined" && <span className="shot-badge shot-badge-hidden">Hidden</span>}
        </a>
      ))}
    </span>
  );
}

// -- Drop zone -------------------------------------------------------------------------

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** Files from a paste: screenshots copied with Win+Shift+S / Cmd+Ctrl+Shift+4, or copied files. */
function pastedFiles(e: ClipboardEvent): File[] {
  const d = e.clipboardData;
  if (!d) return [];
  const files = Array.from(d.files ?? []);
  if (files.length) return files;
  return Array.from(d.items ?? [])
    .filter((i) => i.kind === "file")
    .map((i) => i.getAsFile())
    .filter((f): f is File => !!f);
}

export function DropZone({ onFiles, config, disabled }: { onFiles: (files: File[]) => void; config: MediaConfig | null; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  // paste anywhere while the dialog is open; a plain-text paste still goes to the text field
  useEffect(() => {
    if (disabled) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = pastedFiles(e);
      if (!files.length) return;
      e.preventDefault();
      onFiles(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [onFiles, disabled]);
  const mb = (b?: number) => (b ? Math.round(b / (1024 * 1024)) : null);
  return (
    <div
      className={`dropzone${over ? " over" : ""}${disabled ? " off" : ""}`}
      onDragOver={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        setOver(false);
        onFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <div className="dz-main">
        <span>Paste a screenshot, drop files here, or</span>
        <button type="button" className="btn" disabled={disabled} onClick={() => input.current?.click()}>
          Choose files
        </button>
      </div>
      <div className="dz-hint">
        Images (PNG, JPEG, WebP, GIF) up to {mb(config?.image.maxBytes) ?? 15} MB &middot; recordings (MP4, WebM, MOV) up to {mb(config?.video.maxBytes) ?? 200} MB
      </div>
      <input
        ref={input}
        type="file"
        hidden
        multiple
        accept={ACCEPT}
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
    </div>
  );
}

// -- Upload queue ------------------------------------------------------------------------

export type UploadState = "queued" | "uploading" | "processing" | "done" | "error";

export interface Upload {
  key: string;
  file: File;
  kind: "image" | "video" | null;
  preview: string | null;
  state: UploadState;
  sent: number;
  error: string | null;
  result: UploadResult | null;
}

let seq = 0;

export function useMediaConfig() {
  const [cfg, setCfg] = useState<MediaConfig | null>(null);
  useEffect(() => {
    let live = true;
    mediaConfig().then(
      (c) => live && setCfg(c),
      () => {},
    );
    return () => {
      live = false;
    };
  }, []);
  return cfg;
}

/** A list of files to upload to an item, one at a time, with progress. */
export function useUploads(config: MediaConfig | null) {
  const [list, setList] = useState<Upload[]>([]);
  const [busy, setBusy] = useState(false);
  const listRef = useRef(list);
  listRef.current = list;
  const patch = (key: string, p: Partial<Upload>) => setList((cur) => cur.map((u) => (u.key === key ? { ...u, ...p } : u)));

  useEffect(
    () => () => {
      for (const u of listRef.current) if (u.preview) URL.revokeObjectURL(u.preview);
    },
    [],
  );

  const add = useCallback(
    (files: File[]) => {
      const next = files.map((file): Upload => {
        const kind = kindOf(file);
        const limit = kind === "video" ? config?.video.maxBytes : config?.image.maxBytes;
        const error = !kind
          ? "Only PNG, JPEG, WebP and GIF images, and MP4, WebM and MOV videos can be added."
          : limit && file.size > limit
            ? `${kind === "video" ? "Recordings" : "Images"} can be up to ${Math.round(limit / (1024 * 1024))} MB; this one is ${fmtBytes(file.size)}.`
            : null;
        return {
          key: `u${++seq}`,
          file,
          kind,
          preview: kind === "image" && !error ? URL.createObjectURL(file) : null,
          state: error ? "error" : "queued",
          sent: 0,
          error,
          result: null,
        };
      });
      setList((cur) => [...cur, ...next]);
    },
    [config],
  );

  const remove = useCallback((key: string) => {
    setList((cur) => {
      const u = cur.find((x) => x.key === key);
      if (u?.preview) URL.revokeObjectURL(u.preview);
      return cur.filter((x) => x.key !== key);
    });
  }, []);

  /** Upload everything queued (and retry failed uploads) to #num, or to its note `noteId`. Resolves true if all went up. */
  const run = useCallback(async (num: number, opts: { retry?: boolean; noteId?: string | null } = {}): Promise<boolean> => {
    const target: UploadTarget = { num, noteId: opts.noteId ?? null };
    setBusy(true);
    let ok = true;
    try {
      const todo = listRef.current.filter((u) => u.state === "queued" || (opts.retry && u.state === "error" && u.kind));
      for (const u of todo) {
        patch(u.key, { state: "uploading", sent: 0, error: null });
        try {
          const result = await uploadFile(target, u.file, (sent, total) => patch(u.key, { sent, state: sent >= total ? "processing" : "uploading" }));
          patch(u.key, { state: "done", result });
        } catch (err) {
          ok = false;
          patch(u.key, { state: "error", error: err instanceof ApiError ? err.message : "The upload failed. Try again." });
        }
      }
    } finally {
      setBusy(false);
    }
    return ok;
  }, []);

  return { list, busy, add, remove, run };
}

function statusOf(u: Upload, shots: BoardShot[] | undefined): { text: string; tone?: "bad" | "ok" } {
  switch (u.state) {
    case "queued":
      return { text: "Ready to upload" };
    case "uploading":
      return { text: `Uploading ${Math.round((u.sent / Math.max(1, u.file.size)) * 100)}%` };
    case "processing":
      return { text: u.kind === "video" ? "Processing the recording…" : "Processing…" };
    case "error":
      return { text: u.error ?? "The upload failed.", tone: "bad" };
    case "done": {
      if (u.result?.duplicate) return { text: "Already on this item" };
      const s = shots?.find((x) => x.id === u.result?.id);
      const scan = s?.scan ?? u.result?.scan;
      // a direct upload: made ready (copies, thumbnail) after it arrives, then checked
      if (s?.failed) return { text: `It arrived but couldn't be used: ${s.failed}`, tone: "bad" };
      if (s?.processing) return { text: "Uploaded · getting it ready, then checking it for credentials…" };
      if (scan === "quarantined") return { text: quarantineNotice(s?.findings ?? []), tone: "bad" };
      if (scan === "clean") return { text: "Uploaded · checked, visible to everyone", tone: "ok" };
      if (s?.scanFailed) return { text: "Uploaded, but it couldn't be checked for credentials. Only you and admins can see it.", tone: "bad" };
      return { text: "Uploaded · scanning for credentials…" };
    }
  }
}

export function UploadList({ list, shots, onRemove }: { list: Upload[]; shots?: BoardShot[]; onRemove?: (key: string) => void }) {
  if (!list.length) return null;
  return (
    <ul className="uploads">
      {list.map((u) => {
        const st = statusOf(u, shots);
        const pct = u.state === "uploading" ? Math.round((u.sent / Math.max(1, u.file.size)) * 100) : u.state === "queued" || u.state === "error" ? 0 : 100;
        return (
          <li key={u.key} className={`up up-${u.state}`}>
            <span className="up-thumb" aria-hidden="true">
              {u.preview ? <img src={u.preview} alt="" /> : <span className={`up-icon${u.kind === "video" ? " up-video" : ""}`} />}
            </span>
            <span className="up-main">
              <span className="up-name">
                {u.file.name || "Pasted image"} <span className="up-size">{fmtBytes(u.file.size)}</span>
              </span>
              {(u.state === "uploading" || u.state === "processing") && (
                <span className="up-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
                  <span style={{ width: `${pct}%` }} />
                </span>
              )}
              <span className={`up-status${st.tone ? ` ${st.tone}` : ""}`}>{st.text}</span>
            </span>
            {onRemove && (u.state === "queued" || u.state === "error") && (
              <button type="button" className="up-x" aria-label={`Remove ${u.file.name}`} onClick={() => onRemove(u.key)}>
                &times;
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// -- "Add screenshot" on a card -------------------------------------------------------------

export function AttachModal({
  item,
  initialFiles,
  onClose,
  onSaved,
  noteId = null,
}: {
  item: BoardItem;
  initialFiles?: File[];
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  /** to this note of yours on the item (an upload link Claude made for a note) */
  noteId?: string | null;
}) {
  const note = noteId ? item.notes.find((n) => n.id === noteId) : null;
  const config = useMediaConfig();
  const { list, busy, add, remove, run } = useUploads(config);
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !initialFiles?.length) return;
    seeded.current = true;
    add(initialFiles);
  }, [initialFiles, add]);
  // uploads start as soon as files are added
  const queued = list.some((u) => u.state === "queued");
  useEffect(() => {
    if (busy || !queued) return;
    void run(item.num, { noteId }).then(() => onSaved());
  }, [busy, queued, run, item.num, onSaved, noteId]);
  const working = busy || list.some((u) => u.state === "uploading" || u.state === "processing");
  const workingRef = useRef(working);
  workingRef.current = working;
  // Escape / clicking outside doesn't abandon an upload halfway
  const close = useCallback(() => {
    if (!workingRef.current) onClose();
  }, [onClose]);
  return (
    <Modal onClose={close} wide>
      <h3>{noteId ? `Add screenshots to your note on #${item.num}` : `Add screenshots to #${item.num}`}</h3>
      <p>{item.title}</p>
      {note && <p className="note-quote">&ldquo;{note.body.length > 160 ? `${note.body.slice(0, 160)}…` : note.body}&rdquo;</p>}
      <DropZone onFiles={add} config={config} />
      <UploadList list={list} shots={item.shots} onRemove={remove} />
      <div className="row">
        {list.some((u) => u.state === "error" && u.kind) && (
          <button className="btn" disabled={working} onClick={() => void run(item.num, { retry: true, noteId }).then(() => onSaved())}>
            Retry
          </button>
        )}
        <button className="btn primary" disabled={working} onClick={onClose}>
          Done
        </button>
      </div>
      <div className="hint">
        Each file is checked for passwords, keys and tokens before the rest of the team sees it; until then only you and admins can. Blur anything
        secret before you upload. Location and camera details are removed from photos and recordings.
      </div>
    </Modal>
  );
}
